import { Copy, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';

/**
 * Small icon-only button that copies text to the clipboard.
 * Shows a check icon briefly after copying.
 */
export function CopyButton({
  text,
  getHtml,
  className,
  iconClassName,
  dataTutorialId,
  testId,
  ariaLabel = 'Copy to clipboard',
  copiedAriaLabel = 'Copied',
}: {
  text: string;
  /**
   * Produces the rich-text (`text/html`) flavor written alongside `text`, so
   * pasting into Slack or a doc keeps the formatting. Called on click rather
   * than on render — rendering a long transcript should not pay for HTML
   * nobody copied.
   */
  getHtml?: () => string;
  className?: string;
  /** Override the inner icon size (default `h-3 w-3`). */
  iconClassName?: string;
  /** Stable hook for product tours / tests, applied as `data-tutorial-id`. */
  dataTutorialId?: string;
  /** Stable hook for component and integration tests. */
  testId?: string;
  /** Accessible label before the copy action is confirmed. */
  ariaLabel?: string;
  /** Accessible label while the copied confirmation icon is visible. */
  copiedAriaLabel?: string;
}) {
  const { copied, copy } = useCopyToClipboard();
  const ico = iconClassName ?? 'h-3 w-3';

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={className ?? 'h-5 w-5 shrink-0 text-muted-foreground hover:text-foreground'}
      onClick={() => copy(text, { html: getHtml?.() })}
      aria-label={copied ? copiedAriaLabel : ariaLabel}
      data-testid={testId}
      data-tutorial-id={dataTutorialId}
    >
      {copied ? <Check className={cn(ico, 'text-status-success')} /> : <Copy className={ico} />}
    </Button>
  );
}
